import React, { Dispatch, SetStateAction, useEffect, useMemo, useState } from 'react';
import { Modal, View } from 'react-native';
import FormActions from '../../../components/FormActions';
import { createStyleSheet, useStyles } from 'react-native-unistyles';
import FormInput from '../../../components/FormInput';
import AssignmentDropdown from '../../../components/app-agent/AssignmentDropdown';
import { translate } from '../../../services/translate';
import FilterSwitch from '../../../components/Filter/components/FilterSwitch';
import MyAppText from '../../../components/MyAppText';
import { faExclamationCircle, faExclamationTriangle } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import ScheduleDropdown from './ScheduleDropdown';
import { ScrollView } from 'react-native-web-hover';
import UnitFinishEarlyDropdown from '../../../components/app-agent/UnitFinishEarlyDropdown';
import { useAuth } from '../../../context/Auth';

interface ModalProps {
    isModalVisible: boolean;
    setModalVisible: Dispatch<SetStateAction<boolean>>;
    selectedAgentId: number;
    setUnit: Dispatch<SetStateAction<AppAgentUnit | undefined>>;
    unit: AppAgentUnit;
    onConfirm: () => void;
}

export default function EditAgentModal({ selectedAgentId, isModalVisible, setModalVisible, unit, setUnit, onConfirm }: ModalProps) {
    const { styles } = useStyles(styleSheet);
    const { user: authenticatedUser } = useAuth();

    if (!authenticatedUser) {
        return;
    }

    const selectedAgent = useMemo(() => {
        return unit.UnitUsers.find(user => user.id === selectedAgentId);
    }, [unit, selectedAgentId]);

    const rollbackUnit = useMemo(() => {
        return unit;
    }, [isModalVisible]);

    const [agentActive, setAgentActive] = useState<boolean>(false);

    useEffect(() => {
        if (isModalVisible && selectedAgent) {
            setAgentActive(selectedAgent.active);
        }
    }, [isModalVisible, selectedAgent, unit]);

    const closeModal = () => {
        onConfirm();
        setModalVisible(false);
        setAgentActive(false);
    };

    if (!selectedAgent) {
        return <></>;
    }

    function isFormValid() {
        if (!selectedAgent) {
            return false;
        } else if (selectedAgent.unitId !== null && selectedAgent.unitId !== unit.id) {
            return false;
        } else if (!selectedAgent.active && !selectedAgent.UnitUser.unitFinishEarlyReasonId) {
            return false;
        } else if (selectedAgent.active && !selectedAgent.UnitUser.agentScheduleId) {
            return false;
        } else if (unit.UnitUsers.every(user => !user.active)) {
            return false;
        }

        return true;
    }

    return (
        <Modal
            transparent
            visible={isModalVisible}
            animationType='fade'
            onRequestClose={() => setModalVisible(false)}
        >
            <View style={styles.container}>
                <View style={styles.formContainer}>
                    <View style={{ flexDirection: 'column', rowGap: 8 }}>
                        <MyAppText style={styles.header}>{translate('agentEdit')}</MyAppText>
                        <MyAppText style={styles.subheader}>{translate('fillFieldsBelow')}</MyAppText>
                    </View>
                    <ScrollView>
                        <View style={{ flexDirection: 'column', rowGap: 16 }}>
                            <View style={{ flexDirection: 'column', rowGap: 8 }}>
                                <MyAppText style={styles.fieldLabel}>{translate('agentStatus')}</MyAppText>
                                <FilterSwitch
                                    value={agentActive}
                                    label={translate('active')}
                                    onChange={(value) => {
                                        setAgentActive(value);
                                        setUnit(prevUnit => ({
                                            ...prevUnit!,
                                            UnitUsers: prevUnit?.UnitUsers.map(user =>
                                                user.id === selectedAgentId
                                                    ? {
                                                        ...user,
                                                        unitId: value ? prevUnit.id : null,
                                                        active: Boolean(value)
                                                    }
                                                    : user
                                            ) ?? []
                                        }));
                                    }}
                                    useEnhancedStyle
                                    direction='row'
                                    side='right'
                                    disabled={selectedAgent.unitId !== null && selectedAgent.unitId !== unit.id}
                                />
                            </View>
                            {unit?.UnitUsers.every(user => !user.active) ?
                                <View style={styles.cannotEditAgentContainer}>
                                    <FontAwesomeIcon icon={faExclamationCircle} style={{ color: '#B8401B' }} />
                                    <MyAppText>{translate('cannotSaveTeamWithoutActivatedUsers')}</MyAppText>
                                </View> : <></>
                            }
                            {selectedAgent.unitId !== null && selectedAgent.unitId !== unit.id ?
                                <View style={styles.cannotEditAgentContainer}>
                                    <FontAwesomeIcon icon={faExclamationTriangle} color='#B8401B' />
                                    <MyAppText>{translate('agentActiveOtherTeam')}</MyAppText>
                                </View>
                                : <></>
                            }
                            <FormInput
                                disabled={true}
                                onChangeText={() => { }}
                                value={selectedAgent.warName || selectedAgent.name}
                                label={translate('name')}
                                labelStyle={styles.fieldLabel}
                                viewStyle={{ flexDirection: 'column', rowGap: 8 }}
                            />
                            <FormInput
                                disabled={true}
                                onChangeText={() => { }}
                                value={selectedAgent.registry}
                                label={translate('registry')}
                                labelStyle={styles.fieldLabel}
                                viewStyle={{ flexDirection: 'column', rowGap: 8 }}
                            />
                            <AssignmentDropdown
                                setValue={(value) => {
                                    setUnit(prevUnit => ({
                                        ...prevUnit!,
                                        UnitUsers: prevUnit?.UnitUsers.map(user =>
                                            user.id === selectedAgentId
                                                ? {
                                                    ...user,
                                                    UnitUser: {
                                                        ...user.UnitUser,
                                                        assignmentId: value(user.UnitUser.assignmentId || 0),
                                                    },
                                                }
                                                : user
                                        ) ?? []
                                    }));
                                }}
                                labelStyle={styles.fieldLabel}
                                viewStyle={{ flexDirection: 'column', rowGap: 8 }}
                                value={selectedAgent.UnitUser.assignmentId || 0}
                                zIndex={3}
                                disabled={selectedAgent.unitId !== null && selectedAgent.unitId !== unit.id}
                            />
                            {!agentActive ? <UnitFinishEarlyDropdown
                                value={selectedAgent.UnitUser.unitFinishEarlyReasonId || 0}
                                setValue={(value) => {
                                    setUnit(prevUnit => ({
                                        ...prevUnit!,
                                        UnitUsers: prevUnit?.UnitUsers.map(user =>
                                            user.id === selectedAgentId
                                                ? {
                                                    ...user,
                                                    UnitUser: {
                                                        ...user.UnitUser,
                                                        unitFinishEarlyReasonId: value(user.UnitUser.unitFinishEarlyReasonId || 0),
                                                    },
                                                }
                                                : user
                                        ) ?? []
                                    }));
                                }}
                                unitId={unit.id}
                                labelStyle={styles.fieldLabel}
                                viewStyle={{ flexDirection: 'column', rowGap: 8 }}
                            />
                                : <ScheduleDropdown
                                    labelStyle={styles.fieldLabel}
                                    viewStyle={{ flexDirection: 'column', rowGap: 8 }}
                                    value={selectedAgent.UnitUser.agentScheduleId || 0}
                                    setValue={(value) => {
                                        setUnit(prevUnit => ({
                                            ...prevUnit!,
                                            UnitUsers: prevUnit?.UnitUsers.map(user =>
                                                user.id === selectedAgentId
                                                    ? {
                                                        ...user,
                                                        UnitUser: {
                                                            ...user.UnitUser,
                                                            agentScheduleId: value(user.UnitUser.agentScheduleId || 0),
                                                        },
                                                    }
                                                    : user
                                            ) ?? []
                                        }));
                                    }}
                                    zIndex={2}
                                />
                            }
                        </View>
                    </ScrollView>
                    <FormActions
                        onSubmit={closeModal}
                        onClose={() => {
                            setModalVisible(false);
                            setAgentActive(false);
                            setUnit(rollbackUnit);
                        }}
                        disabled={!isFormValid()}
                    />
                </View>
            </View>
        </Modal>
    );
}

const styleSheet = createStyleSheet((theme) => ({
    container: {
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        backgroundColor: '#31313199',
        flex: 1,
        paddingHorizontal: 16,
    },
    formContainer: {
        width: '100%',
        backgroundColor: theme.colors.container.background,
        borderRadius: 4,
        padding: 20,
        rowGap: 32,
        maxHeight: '95%',
        overflow: 'hidden',
    },
    header: {
        fontSize: 18,
        fontWeight: '700',
        flexWrap: 'wrap',
        color: theme.colors.modal.header,
    },
    subheader: {
        fontSize: 14,
        fontWeight: '400',
        flexWrap: 'wrap',
        color: theme.colors.modal.subheader,
    },
    fieldLabel: {
        fontSize: 14,
        fontWeight: '600',
        flexWrap: 'wrap',
        color: theme.colors.modal.header,
    },
    cannotEditAgentContainer: {
        flexDirection: 'row',
        gap: 10,
        justifyContent: 'center',
        alignItems: 'center',
        borderWidth: 1,
        borderRadius: 8,
        borderColor: '#B8401B',
        padding: 10
    }
}));
